/* eslint-disable */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Icon,
  Progress,
  Table,
  Tbody,
  HStack,
  Avatar,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Button,
  Select,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Divider,
  Badge,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
// Custom components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import { NavLink } from 'react-router-dom';
import * as React from 'react';
// Assets
import { MdCancel, MdCheckCircle, MdOutlineError, MdReceipt, MdContentCopy, MdDownload } from 'react-icons/md';
import { ViewIcon } from '@chakra-ui/icons';
import jsPDF from 'jspdf';

import constants from 'providers/Constants';

const columnHelper = createColumnHelper();

// Transaction Details Modal Component
function TransactionDetailsModal({ isOpen, onClose, transaction, t }) {
  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const receiptBg = useColorModeValue('gray.50', 'navy.900');
  const toast = useToast();
  const [showReceipt, setShowReceipt] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const receiptRef = React.useRef(null);

  if (!transaction) return null;

  const downloadReceipt = async () => {
    setIsDownloading(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to load image as base64 using fetch
      const loadImage = async (url) => {
        try {
          // Try fetching the image
          const response = await fetch(url);
          const blob = await response.blob();
          
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          // Fallback: try loading with Image element
          return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
              } catch (e) {
                resolve(null);
              }
            };
            img.onerror = () => resolve(null);
            img.src = url;
          });
        }
      };

      // Helper function to center text
      const centerText = (text, y, fontSize = 12, fontStyle = 'normal') => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        const textWidth = pdf.getTextWidth(text);
        pdf.text(text, (pageWidth - textWidth) / 2, y);
      };

      // Helper function to add a line of text with label and value
      const addDetailRow = (label, value, y) => {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(label, margin + 10, y);
        
        pdf.setTextColor(30, 30, 30);
        pdf.setFont('helvetica', 'bold');
        const valueText = String(value || 'N/A');
        const valueWidth = pdf.getTextWidth(valueText);
        pdf.text(valueText, pageWidth - margin - 10 - valueWidth, y);
        
        return y + 8;
      };

      // Helper to draw avatar placeholder circle with initial
      const drawAvatarPlaceholder = (x, y, size, initial) => {
        // Draw circle background
        pdf.setFillColor(100, 100, 220);
        pdf.circle(x + size / 2, y + size / 2, size / 2, 'F');
        // Draw initial
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const textWidth = pdf.getTextWidth(initial);
        pdf.text(initial, x + (size - textWidth) / 2, y + size / 2 + 2);
      };

      // Draw receipt border
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.roundedRect(margin, margin, contentWidth, 220, 3, 3, 'S');

      yPos = margin + 10;

      // Add avatar
      const avatarSize = 20;
      let avatarLoaded = false;
      
      if (transaction.avatar) {
        try {
          const avatarData = await loadImage(transaction.avatar);
          if (avatarData && avatarData.startsWith('data:')) {
            const format = avatarData.includes('image/png') ? 'PNG' : 'JPEG';
            pdf.addImage(avatarData, format, (pageWidth - avatarSize) / 2, yPos, avatarSize, avatarSize);
            avatarLoaded = true;
          }
        } catch (e) {
          console.log('Could not load avatar image:', e.message);
        }
      }
      
      // Draw placeholder if avatar didn't load
      if (!avatarLoaded) {
        const initial = (transaction.title || 'T').charAt(0).toUpperCase();
        drawAvatarPlaceholder((pageWidth - avatarSize) / 2, yPos, avatarSize, initial);
      }
      
      yPos += avatarSize + 10;

      // Title
      pdf.setTextColor(30, 30, 30);
      centerText(transaction.title || t('Transaction'), yPos, 16, 'bold');
      yPos += 7;

      // Subtitle
      pdf.setTextColor(100, 100, 100);
      centerText(transaction.sub_title || '', yPos, 10, 'normal');
      yPos += 10;

      // Status badge
      const statusKey = transaction.status?.charAt(0).toUpperCase() + transaction.status?.slice(1).toLowerCase();
      const status = t(statusKey || 'N/A').toUpperCase();
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const statusWidth = pdf.getTextWidth(status) + 10;
      const statusX = (pageWidth - statusWidth) / 2;
      
      // Status background color
      if (transaction.status?.toLowerCase() === 'completed') {
        pdf.setFillColor(198, 246, 213);
        pdf.setTextColor(34, 84, 61);
      } else if (transaction.status?.toLowerCase() === 'pending') {
        pdf.setFillColor(254, 235, 200);
        pdf.setTextColor(116, 66, 16);
      } else if (transaction.status?.toLowerCase() === 'failed') {
        pdf.setFillColor(254, 215, 215);
        pdf.setTextColor(130, 39, 39);
      } else {
        pdf.setFillColor(226, 232, 240);
        pdf.setTextColor(45, 55, 72);
      }
      
      pdf.roundedRect(statusX, yPos - 4, statusWidth, 7, 3, 3, 'F');
      pdf.text(status, statusX + 5, yPos + 1);
      yPos += 15;

      // Dashed line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
      yPos += 10;

      // Amount
      const amount = `${transaction.country_currency_symbol || ''}${Number(transaction.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      pdf.setTextColor(30, 30, 30);
      centerText(amount, yPos, 24, 'bold');
      yPos += 8;

      // Currency full name
      pdf.setTextColor(100, 100, 100);
      centerText(transaction.country_currency_full || '', yPos, 10, 'normal');
      yPos += 12;

      // Dashed line
      pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
      yPos += 10;

      // Transaction details
      pdf.setTextColor(30, 30, 30);
      yPos = addDetailRow(t('Transaction ID'), transaction.transaction_id, yPos);
      yPos = addDetailRow(t('Reference'), transaction.trovilo_payment_reference, yPos);
      yPos = addDetailRow(t('Type'), transaction.type?.charAt(0).toUpperCase() + transaction.type?.slice(1), yPos);
      yPos = addDetailRow(t('Currency'), transaction.currency, yPos);
      
      if (transaction.amount_paid) {
        yPos = addDetailRow(t('Amount Paid'), `${transaction.currency_paid || ''} ${Number(transaction.amount_paid).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, yPos);
      }
      
      if (transaction.fee) {
        yPos = addDetailRow(t('Fee'), `${transaction.currency || ''} ${Number(transaction.fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, yPos);
      }
      
      yPos = addDetailRow(t('Status Message'), transaction.status_message, yPos);
      yPos = addDetailRow(t('Date'), transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : t('N/A'), yPos);
      yPos += 5;

      // Dashed line
      pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
      yPos += 10;

      // Footer
      pdf.setTextColor(100, 100, 100);
      centerText(t('Thank you for using BlockMap'), yPos, 10, 'normal');
      yPos += 6;
      centerText(transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '', yPos, 8, 'normal');

      // Save PDF
      pdf.save(`BlockMap_Receipt_${transaction.transaction_id || 'transaction'}.pdf`);

      toast({
        title: t('Receipt downloaded successfully!'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: t('Failed to download receipt'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'pending': return 'orange';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount, currency) => {
    const num = Number(amount);
    if (isNaN(num)) return 'N/A';
    return `${currency || ''} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t('Copied!'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const DetailRow = ({ label, value, copyable = false }) => (
    <Flex justify="space-between" align="center" py={2}>
      <Text color={secondaryTextColor} fontSize="sm">{label}</Text>
      <HStack>
        <Text color={textColor} fontSize="sm" fontWeight="600" textAlign="right" maxW="200px" isTruncated>
          {value || 'N/A'}
        </Text>
        {copyable && value && (
          <IconButton
            size="xs"
            variant="ghost"
            icon={<Icon as={MdContentCopy} />}
            onClick={() => copyToClipboard(value)}
            aria-label="Copy"
          />
        )}
      </HStack>
    </Flex>
  );

  // Receipt View
  const ReceiptView = () => (
    <Box
      ref={receiptRef}
      bg={receiptBg}
      borderRadius="lg"
      p={6}
      border="1px dashed"
      borderColor={borderColor}
    >
      <VStack spacing={4} align="stretch">
        {/* Receipt Header */}
        <Flex direction="column" align="center" pb={4} borderBottom="1px dashed" borderColor={borderColor}>
          {transaction.avatar && (
            <Avatar src={transaction.avatar} size="lg" mb={3} />
          )}
          <Text fontSize="xl" fontWeight="bold" color={textColor}>{transaction.title}</Text>
          <Text fontSize="sm" color={secondaryTextColor}>{transaction.sub_title}</Text>
          <Badge colorScheme={getStatusColor(transaction.status)} mt={2} fontSize="sm" px={3} py={1} borderRadius="full">
            {transaction.status?.toUpperCase()}
          </Badge>
        </Flex>

        {/* Amount Section */}
        <Box textAlign="center" py={4} borderBottom="1px dashed" borderColor={borderColor}>
          <Text fontSize="3xl" fontWeight="bold" color={textColor}>
            {transaction.country_currency_symbol}{Number(transaction.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>{transaction.country_currency_full}</Text>
        </Box>

        {/* Receipt Details */}
        <VStack spacing={0} align="stretch">
          <DetailRow label={t('Transaction ID')} value={transaction.transaction_id} copyable />
          <DetailRow label={t('Reference')} value={transaction.trovilo_payment_reference} copyable />
          <DetailRow label={t('Type')} value={transaction.type?.charAt(0).toUpperCase() + transaction.type?.slice(1)} />
          <DetailRow label={t('Currency')} value={transaction.currency} />
          {transaction.amount_paid && (
            <DetailRow label={t('Amount Paid')} value={formatAmount(transaction.amount_paid, transaction.currency_paid)} />
          )}
          {transaction.fee && (
            <DetailRow label={t('Fee')} value={formatAmount(transaction.fee, transaction.currency)} />
          )}
          <DetailRow label={t('Status Message')} value={transaction.status_message} />
          <DetailRow label={t('Date')} value={formatDate(transaction.createdAt)} />
        </VStack>

        {/* Receipt Footer */}
        <Flex direction="column" align="center" pt={4} borderTop="1px dashed" borderColor={borderColor}>
          <Text fontSize="xs" color={secondaryTextColor}>{t('Thank you for using BlockMap')}</Text>
          <Text fontSize="xs" color={secondaryTextColor} mt={1}>
            {formatDate(transaction.createdAt)}
          </Text>
        </Flex>
      </VStack>
    </Box>
  );

  // Details View
  const DetailsView = () => (
    <VStack spacing={4} align="stretch">
      {/* Transaction Header */}
      <Flex align="center" gap={4}>
        {transaction.avatar && (
          <Avatar src={transaction.avatar} size="md" />
        )}
        <Box>
          <Text fontSize="lg" fontWeight="bold" color={textColor}>{transaction.title}</Text>
          <Text fontSize="sm" color={secondaryTextColor}>{transaction.sub_title}</Text>
        </Box>
        <Badge colorScheme={getStatusColor(transaction.status)} ml="auto" fontSize="sm">
          {transaction.status?.toUpperCase()}
        </Badge>
      </Flex>

      <Divider />

      {/* Transaction Info */}
      <Box>
        <Text fontWeight="600" color={textColor} mb={3}>{t('Transaction Information')}</Text>
        <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
          <DetailRow label={t('Transaction ID')} value={transaction.transaction_id} copyable />
          <DetailRow label={t('Reference')} value={transaction.trovilo_payment_reference} copyable />
          <DetailRow label={t('Type')} value={transaction.type?.charAt(0).toUpperCase() + transaction.type?.slice(1)} />
        </VStack>
      </Box>

      {/* Amount Info */}
      <Box>
        <Text fontWeight="600" color={textColor} mb={3}>{t('Amount Details')}</Text>
        <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
          <DetailRow label={t('Amount')} value={formatAmount(transaction.amount, transaction.country_currency_short)} />
          <DetailRow label={t('Currency')} value={transaction.country_currency_full} />
          {transaction.amount_paid && (
            <DetailRow label={t('Amount Paid')} value={formatAmount(transaction.amount_paid, transaction.currency_paid)} />
          )}
          {transaction.fee && (
            <DetailRow label={t('Fee')} value={formatAmount(transaction.fee, transaction.currency)} />
          )}
        </VStack>
      </Box>

      {/* Status Info */}
      <Box>
        <Text fontWeight="600" color={textColor} mb={3}>{t('Status Information')}</Text>
        <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
          <DetailRow label={t('Status')} value={transaction.status?.toUpperCase()} />
          <DetailRow label={t('Status Message')} value={transaction.status_message} />
          <DetailRow label={t('Refunded')} value={transaction.refunded ? t('Yes') : t('No')} />
          <DetailRow label={t('Created At')} value={formatDate(transaction.createdAt)} />
          <DetailRow label={t('Updated At')} value={formatDate(transaction.updatedAt)} />
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <ModalContent bg={bgColor} borderRadius="xl">
          <ModalHeader>
            <Flex align="center" gap={2}>
              <Icon as={showReceipt ? MdReceipt : ViewIcon} />
              <Text>{showReceipt ? t('Transaction Receipt') : t('Transaction Details')}</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {showReceipt ? <ReceiptView /> : <DetailsView />}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={() => setShowReceipt(!showReceipt)}
              leftIcon={<Icon as={showReceipt ? ViewIcon : MdReceipt} />}
            >
              {showReceipt ? t('View Details') : t('View Receipt')}
            </Button>
            <Button
              variant="outline"
              colorScheme="green"
              mr={3}
              onClick={downloadReceipt}
              leftIcon={<Icon as={MdDownload} />}
              isLoading={isDownloading}
              loadingText={t('Downloading...')}
            >
              {t('Download Receipt')}
            </Button>
            <Button colorScheme="brand" onClick={onClose}>
              {t('Close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function FiatTransactionsTable(props) {
  const { tableData, sortArray, index } = props;
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState([]);
  const [data, setData] = React.useState(tableData);
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  React.useEffect(() => {
    setData(tableData);
  }, [tableData]);

  const columns = [
    columnHelper.accessor('title', {
      id: 'title',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('TITLE')}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('sub_title', {
      id: 'sub_title',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('REMARKS')}
        </Text>
      ),
      cell: (info) => {
        const value = info.getValue();
        const avatar = info?.row?.original?.avatar;
        return (
          <HStack spacing={2} align="center">
            {avatar ? (
              <Avatar src={avatar} name={String(value || 'Transaction')} size="xs" />
            ) : null}
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {info.getValue()}
            </Text>
          </HStack>
        );
      }
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('AMOUNT')}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.row.original.country_currency_short} {Number(info.getValue()).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('STATUS')}
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const translatedStatus = status ? t(status.toUpperCase()) : t('N/A');
        return (
          <Flex align="center">
            <Icon
              w="24px"
              h="24px"
              me="5px"
              color={
                status === 'completed'
                  ? 'green.500'
                  : status === 'pending'
                    ? 'orange.500'
                    : status === 'failed'
                      ? 'red.500'
                      : null
              }
              as={
                status === 'completed'
                  ? MdCheckCircle
                  : status === 'pending'
                    ? MdOutlineError
                    : status === 'failed'
                      ? MdCancel
                      : null
              }
            />
            <Text color={textColor} fontSize="sm" fontWeight="700">
              {translatedStatus}
            </Text>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('DATE')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue() && !isNaN(Date.parse(info.getValue()))
            ? new Date(info.getValue()).toLocaleDateString()
            : 'Invalid date'}
        </Text>
      ),
    }),
    columnHelper.accessor('transaction_id', {
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t('ACTIONS')}
        </Text>
      ),
      cell: (info) => (
        <Button
          size="xs"
          colorScheme="brand"
          variant="outline"
          leftIcon={<Icon as={MdReceipt} />}
          fontSize="10px"
          height="24px"
          onClick={() => handleViewTransaction(info.row.original)}
        >
          {t('View')}
        </Button>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: { pageSize, pageIndex },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    setPageSize: pageSize,
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    table.setPageIndex(0);
  }, [tableData, table]);

  const getNextPage = () => {
    table.nextPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  };

  const getPreviousPage = () => {
    table.previousPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  };

  return (
    <>
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflow="hidden"
    >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          {t('Transactions')}
        </Text>
      </Flex>
      <Box overflowX="auto">
        {data.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {t('No results found!')} {t('Check')} <NavLink style={{ color: 'green' }} to={constants.urls.documentation} >{t('API documentation')}</NavLink> {t('for more details')}
            </Text>
          </Flex>
        ) : (
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          justifyContent="space-between"
                          align="center"
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color="gray.400"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: '',
                            desc: '',
                          }[header.column.getIsSorted()] ?? null}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table
                .getRowModel()
                .rows.slice(0, 11)
                .map((row) => {
                  return (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <Td
                            key={cell.id}
                            fontSize={{ sm: '14px' }}
                            minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                            borderColor="transparent"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        )}
      </Box>
      <Flex justifyContent="space-between" alignItems="center" px="25px" py="10px">
        <Button onClick={getPreviousPage} isDisabled={!table.getCanPreviousPage()}>
          {t('Previous')}
        </Button>
        <Text>
          {t('Page')} {table.getState().pagination.pageIndex + 1} {t('of')} {table.getPageCount()}
        </Text>
        <Button onClick={getNextPage} isDisabled={!table.getCanNextPage()}>
          {t('Next')}
        </Button>
        <Select
          value={pageSize}
          width="100px"
          onChange={(e) => {
            setPagination((prev) => ({
              ...prev,
              pageSize: Number(e.target.value),
            }));
          }}
        >
          {sortArray.map((size) => (
            <option key={size} value={size}>
              {t('Show')} {size} {t('Max')}
            </option>
          ))}
        </Select>
      </Flex>
    </Card>

    {/* Transaction Details Modal */}
    <TransactionDetailsModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      transaction={selectedTransaction}
      t={t}
    />
    </>
  );
}
